import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { normalizeMathMarkdown } from '../../utils/mathText';

interface RichTextProps {
  /** AI-generated text: may contain markdown and LaTeX in any delimiter style. */
  text: string | null | undefined;
  /**
   * Inline mode renders without block wrappers, so it is safe inside buttons,
   * list items, labels and table cells (quiz options, answers, titles).
   */
  inline?: boolean;
  className?: string;
}

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS: any[] = [[rehypeKatex, { throwOnError: false, strict: false }]];

// In inline mode every block element collapses to a span, so the output is
// valid inside <button>/<li>/<p> and keeps the surrounding layout.
const InlineBlock: React.FC<{ children?: React.ReactNode }> = ({ children }) => <span>{children}</span>;
const INLINE_COMPONENTS: Components = {
  p: InlineBlock,
  h1: InlineBlock,
  h2: InlineBlock,
  h3: InlineBlock,
  h4: InlineBlock,
  h5: InlineBlock,
  h6: InlineBlock,
  ul: InlineBlock,
  ol: InlineBlock,
  li: InlineBlock,
  blockquote: InlineBlock,
  pre: InlineBlock,
};

/**
 * The one component for rendering AI-generated text anywhere in the app.
 * Use it instead of printing `{text}` directly for questions, options,
 * answers, explanations and feedback, so math always renders.
 *
 * Raw HTML in the text is NOT rendered (no rehype-raw): this text comes from
 * an LLM, and treating it as HTML would be an XSS vector.
 */
const RichText: React.FC<RichTextProps> = ({ text, inline = false, className }) => {
  const markdown = normalizeMathMarkdown(text, { inline });
  if (!markdown) return null;

  const Wrapper = inline ? 'span' : 'div';
  return (
    <Wrapper className={['rich-text', className].filter(Boolean).join(' ')}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={inline ? INLINE_COMPONENTS : undefined}
      >
        {markdown}
      </ReactMarkdown>
    </Wrapper>
  );
};

export default RichText;
